import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { artPages, fitPageWidth, ShareRef, STACK_GAP, type ArtPage, type PanelView, type PublicShareView, type ShareEvent } from '@lc/contracts';
import { api, ApiError } from '../api';
import { useI18n } from '../i18n';
import { AppleGlyph } from '../ui/glyphs';
import { MangaPanel } from '../ui/manga';
import { Splash } from '../ui/Splash';
import { RESIDENTS } from './features-content';

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL;
/** Each side this share of the screen turns the page when tapped; the middle shows or hides the bars. */
const EDGE = 0.3;
/** A drag this share of the screen wide turns the page. */
const SWIPE = 0.18;
/** The scrolling strip's widest. */
const MAX_STRIP = 640;
/** The paged reader's widest page: a wide screen reads a phone-like column, not panels blown up to fill it. */
const COLUMN = 760;
/** The bars' own heights (the CSS sets them), above the safe-area insets, and the gap the art keeps from them. */
const TOP_BAR = 58;
const BOTTOM_BAR = 36;
const ART_GAP = 8;
/** How far panels may narrow to share a page. */
const PAIR_SCALE = 0.85;
/** On a two-page spread a page is half the screen, so its panels may narrow further to stack. */
const WIDE_PAIR_SCALE = 0.72;
/** A wide screen opens like a book: two pages side by side, this far apart. */
const SPREAD_GAP = 24;
const isWide = (w: number, h: number) => w >= 1024 && w >= h * 1.2;
/** How long the chapter's pictures may take before it opens anyway. */
const PRELOAD_MS = 8000;

type Mode = 'paged' | 'scroll';
type Dir = 'ltr' | 'rtl';
/** A page of the reader: art (one page, or two side by side on a wide screen), or the cover or 完. */
type Item = { key: string; arts: ArtPage[] } | { key: string; node: ReactNode };
type Report = (event: ShareEvent['event']) => void;

/**
 * Tells the sharer how the link is doing: one view and one read to 完 per visitor and tab session,
 * every tap into the game; with where the link was posted (`?ref=`).
 */
function useShareReport(token: string, ref: string | undefined): Report {
  return useCallback(
    (event) => {
      const key = `inked.share.${event}.${token}`;
      if (event !== 'cta') {
        try {
          if (sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, '1');
        } catch {
          // ignore: counted again on the next visit
        }
      }
      api.shareEvent(token, { event, ...(ref ? { ref } : {}) });
    },
    [token, ref],
  );
}

/** A per-browser reading choice, kept across visits when storage allows. */
function useStored<T extends string>(key: string, initial: T, allowed: readonly T[]): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const s = localStorage.getItem(key) as T | null;
      return s && allowed.includes(s) ? s : initial;
    } catch {
      return initial;
    }
  });
  const set = (next: T) => {
    setV(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      // ignore
    }
  };
  return [v, set];
}

/** The safe-area insets, read off a probe (CSS knows them, script doesn't). */
function useSafeArea() {
  const [inset, setInset] = useState({ top: 0, bottom: 0 });
  useEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
    document.body.appendChild(probe);
    const cs = getComputedStyle(probe);
    setInset({ top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 });
    probe.remove();
  }, []);
  return inset;
}

function useViewport() {
  const read = () => ({ w: window.innerWidth, h: window.innerHeight });
  const [size, setSize] = useState(read);
  useEffect(() => {
    const on = () => setSize(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return size;
}

/**
 * A chapter someone shared, read the way the app reads it: the cover, its pages one at a time on ink
 * (swipe, tap a side or use the arrow keys; tap the middle for the bars), then 完 and a way into the
 * game. Or the whole chapter as one scrolling strip; and pages can turn either way.
 */
export function SharePage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const from = ShareRef.safeParse(params.get('ref'));
  const report = useShareReport(token, from.success ? from.data : undefined);
  const { t } = useI18n();
  const [share, setShare] = useState<PublicShareView | null>(null);
  const [failed, setFailed] = useState<'gone' | 'error' | null>(null);
  // The app's opening plays while the whole chapter loads (every picture), then lifts off it.
  const [revealed, setRevealed] = useState(false);
  const [opened, setOpened] = useState(false);
  const load = useCallback(() => {
    setFailed(null);
    setShare(null);
    api.publicShare(token).then(
      async (s) => {
        await preload([...coverImages(s), ...s.panels.map((p) => p.imageUrl)]);
        setShare(s);
      },
      (err: unknown) => setFailed(err instanceof ApiError && err.status === 404 ? 'gone' : 'error'),
    );
  }, [token]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!share) return;
    document.title = `${share.title} · ${t('footer.copy').replace('© ', '')}`;
    report('view');
  }, [share, t, report]);

  // A chapter that can't open says so: the page's own splash (index.html) mustn't stay over it.
  useEffect(() => {
    if (failed) document.getElementById('boot-splash')?.remove();
  }, [failed]);

  if (failed) {
    return (
      <div className="sr-note">
        <p className="sr-note-title">{t(failed === 'gone' ? 'share.gone.title' : 'share.error')}</p>
        {failed === 'gone' ? <p>{t('share.gone.body')}</p> : null}
        {failed === 'gone' ? (
          <Link className="btn btn-pink" to="/">
            {t('share.gone.home')}
          </Link>
        ) : (
          <button className="btn btn-ink" onClick={load}>
            {t('share.retry')}
          </button>
        )}
      </div>
    );
  }
  return (
    <>
      {share && revealed ? <ShareReader share={share} report={report} /> : null}
      {opened ? null : <Splash ready={!!share} waiting={t('share.loading')} onLeave={() => setRevealed(true)} onDone={() => setOpened(true)} />}
    </>
  );
}

function ShareReader({ share, report }: { share: PublicShareView; report: Report }) {
  const { t } = useI18n();
  const { w, h } = useViewport();
  const [mode, setMode] = useStored<Mode>('inked.share.mode', 'paged', ['paged', 'scroll']);
  const [dir, setDir] = useStored<Dir>('inked.share.dir', 'ltr', ['ltr', 'rtl']);
  const [settings, setSettings] = useState(false);
  // The art sits between the bars (they're up on the first page), in a column no wider than a phone's reading width.
  const safe = useSafeArea();
  const wide = isWide(w, h);
  const pageW = wide ? Math.min(Math.floor((w - 48 - SPREAD_GAP) / 2), COLUMN) : Math.min(w, COLUMN);
  const room = h - safe.top - TOP_BAR - safe.bottom - BOTTOM_BAR - ART_GAP * 2;
  // The bars take room the app's reader doesn't lose, so panels may narrow a little to pair up.
  const pages = useMemo(() => artPages(share.panels, pageW, room, wide ? WIDE_PAIR_SCALE : PAIR_SCALE), [share.panels, pageW, room, wide]);
  // On a wide screen the pages open two at a time, like a book.
  const spreads = useMemo(() => (wide ? pages.flatMap((_, i) => (i % 2 ? [] : [pages.slice(i, i + 2)])) : pages.map((pg) => [pg])), [pages, wide]);
  const [at, setAt] = useState(0);

  const cover = (start: ReactNode) => <Cover share={share} start={start} />;
  const end = <End share={share} report={report} onAgain={() => (mode === 'paged' ? setAt(1) : window.scrollTo({ top: 0, behavior: 'smooth' }))} />;
  const bar = (
    <div className="sr-actions">
      <button className="sr-icon" aria-label={t('share.settings')} aria-expanded={settings} onClick={() => setSettings((s) => !s)}>
        <SlidersGlyph />
      </button>
    </div>
  );
  const sheet = settings ? <Settings mode={mode} dir={dir} onMode={setMode} onDir={setDir} onClose={() => setSettings(false)} /> : null;

  if (mode === 'scroll') {
    const stripW = Math.min(w - 16, MAX_STRIP);
    return (
      <div className="sr-scroll">
        <header className="sr-scroll-bar">
          <Link to="/" className="sr-brand">
            落筆成戀
          </Link>
          <span className="sr-scroll-title">{share.title}</span>
          {bar}
        </header>
        {cover(null)}
        <div className="sr-strip">
          {share.panels.map((p) => (
            <MangaPanel key={p.panelId} panel={p} width={stripW} />
          ))}
        </div>
        {end}
        {sheet}
      </div>
    );
  }

  const start = (
    <div className="sr-start">
      <button className="sr-start-btn" onClick={() => setAt(1)}>
        <small>{t('share.pages', { n: pages.length })}</small>
        <b>{t('share.start')}</b>
        <ArrowGlyph flip={dir === 'rtl'} />
      </button>
      <p className="sr-hint">{t(dir === 'rtl' ? 'share.swipeRtl' : 'share.swipeLtr')}</p>
      <p className="sr-hint sr-keys">{t('share.keys')}</p>
    </div>
  );
  const items: Item[] = [{ key: 'cover', node: cover(start) }, ...spreads.map((arts) => ({ key: arts[0]!.key, arts })), { key: 'end', node: end }];
  return (
    <>
      <Pager
        items={items}
        at={Math.min(at, items.length - 1)}
        onAt={(i) => {
          setAt(i);
          if (i === items.length - 1) report('end');
        }}
        dir={dir}
        w={w}
        h={h}
        pageW={pageW}
        room={room}
        title={share.title}
        pages={pages.length}
        spreads={spreads.length}
        actions={bar}
      />
      {sheet}
    </>
  );
}

/** The pages side by side on ink, one in view; the bars over it go away once a page is turned. */
function Pager({ items, at, onAt, dir, w, h, pageW, room, title, pages, spreads, actions }: { items: Item[]; at: number; onAt: (i: number) => void; dir: Dir; w: number; h: number; pageW: number; room: number; title: string; pages: number; spreads: number; actions: ReactNode }) {
  const { t } = useI18n();
  const [bars, setBars] = useState(true);
  const [drag, setDrag] = useState<number | null>(null);
  const start = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const last = items.length - 1;
  const rtl = dir === 'rtl';
  // Right-to-left lays the pages out the other way round, so the next one lies to the left.
  const slot = rtl ? last - at : at;
  const here = items[at]!;
  const onArt = 'arts' in here;
  // The page numbers in view: 3, or 3–4 on a spread.
  const first = items.slice(1, at).reduce((n, it) => n + ('arts' in it ? it.arts.length : 0), 1);
  const seen = onArt ? (here.arts.length > 1 ? `${first}–${first + here.arts.length - 1}` : String(first)) : '';

  const go = useCallback(
    (i: number) => {
      const next = Math.min(last, Math.max(0, i));
      if (next === at) return;
      onAt(next);
      setBars(false);
    },
    [at, last, onAt],
  );
  const forward = (towardsRight: boolean) => (towardsRight !== rtl ? 1 : -1);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowRight') go(at + forward(true));
      else if (e.key === 'ArrowLeft') go(at + forward(false));
      else if (e.key === ' ' || e.key === 'PageDown') go(at + 1);
      else if (e.key === 'PageUp') go(at - 1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Prefetch the next two pages so a turn never shows a blank.
  useEffect(() => {
    for (const it of items.slice(at + 1, at + 3)) if ('arts' in it) for (const p of it.arts.flatMap((a) => a.panels)) new Image().src = p.imageUrl;
  }, [at, items]);

  const down = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    start.current = { x: e.clientX, y: e.clientY, moved: false };
  };
  const move = (e: ReactPointerEvent) => {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (!s.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(e.clientY - s.y)) s.moved = true;
    if (s.moved) setDrag(dx);
  };
  const up = (e: ReactPointerEvent) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    setDrag(null);
    if (s.moved) {
      if (Math.abs(dx) > w * SWIPE) go(at + forward(dx < 0));
      return;
    }
    // A tap: the sides turn the page on the art, the middle shows or hides the bars.
    if (!onArt || (e.target instanceof HTMLElement && e.target.closest('a,button'))) return;
    const x = e.clientX;
    if (x > w * EDGE && x < w * (1 - EDGE)) setBars((b) => !b);
    else go(at + forward(x >= w * (1 - EDGE)));
  };

  const shown = bars || !onArt;
  return (
    <div className={`sr-pager${onArt ? ' sr-on-art' : ''}`} style={{ height: h }}>
      <div
        className="sr-track"
        style={{ width: w * items.length, transform: `translate3d(${-slot * w + (drag ?? 0)}px,0,0)`, transition: drag === null ? undefined : 'none' }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={() => {
          start.current = null;
          setDrag(null);
        }}
      >
        {(rtl ? [...items].reverse() : items).map((it, i) => {
          // Pages either side of the one in view sit back, dimmed and turned away, so a turn reads as the
          // page leaving and the next one coming up (left and right follow the layout, not the reading order).
          const side = i === slot ? 'sr-here' : i < slot ? 'sr-left' : 'sr-right';
          return (
            <section key={it.key} className={`sr-slide ${side}${'arts' in it ? ' sr-art' : ''}`} style={{ width: w, height: h }} aria-hidden={it !== items[at]}>
              <div className="sr-page">
                {'arts' in it ? (
                  // Two pages read in the reading direction: right to left puts the first on the right.
                  <div className="sr-pair" style={{ gap: SPREAD_GAP, flexDirection: rtl ? 'row-reverse' : 'row' }}>
                    {it.arts.map((art) => (
                      // Both pages at the same panel width, so a page of one panel doesn't outsize its neighbour.
                      <Spread key={art.key} page={art} width={Math.min(...it.arts.map((a) => fitPageWidth(a.panels, pageW, room)))} />
                    ))}
                  </div>
                ) : (
                  it.node
                )}
              </div>
            </section>
          );
        })}
      </div>

      <header className={`sr-top${shown ? '' : ' sr-hidden'}`}>
        <Link to="/" className="sr-brand" aria-label="落筆成戀 Inked in Love">
          落筆成戀
        </Link>
        <div className="sr-title">
          {onArt ? (
            <>
              <b>{title}</b>
              <span>{t('share.pageOf', { n: seen, total: pages })}</span>
            </>
          ) : null}
        </div>
        {actions}
      </header>
      {onArt ? (
        <nav className={`sr-ticks${shown ? '' : ' sr-hidden'}${rtl ? ' sr-rtl' : ''}`} aria-label={t('share.pageOf', { n: seen, total: pages })}>
          {Array.from({ length: spreads }, (_, i) => (
            <button key={i} className={i < at ? 'sr-read' : ''} aria-label={t('share.goPage', { n: i + 1 })} aria-current={i === at - 1 ? 'page' : undefined} onClick={() => go(i + 1)} />
          ))}
        </nav>
      ) : null}
    </div>
  );
}

/** A page's panels stacked edge to edge at a width that fits the screen (fitPageWidth). */
function Spread({ page, width }: { page: ArtPage<PanelView>; width: number }) {
  return (
    <div className="sr-spread" style={{ gap: STACK_GAP }}>
      {page.panels.map((p) => (
        <MangaPanel key={p.panelId} panel={p} width={width} />
      ))}
    </div>
  );
}

/** The cover's lead, when they're one of the residents the website has art for. */
const leadOf = (share: PublicShareView) => (share.leadNpcId && RESIDENTS.some((r) => r.id === share.leadNpcId) ? share.leadNpcId : null);

/** The pictures the cover shows. */
function coverImages(share: PublicShareView): string[] {
  const lead = leadOf(share);
  return lead ? [`/art/bg_${lead}.webp`, `/art/fig_${lead}.webp`] : share.coverUrl ? [share.coverUrl] : [];
}

/** Fetches pictures ahead; done when they're in, failed, or after PRELOAD_MS, whichever comes first. */
function preload(urls: string[]): Promise<void> {
  const each = urls.map((src) => new Promise<void>((done) => Object.assign(new Image(), { onload: done, onerror: done, src })));
  return Promise.race([Promise.all(each).then(() => undefined), new Promise<void>((r) => setTimeout(r, PRELOAD_MS))]);
}

/** Before the pages: the chapter's lead on their place (or its cover), its number, day and title. */
function Cover({ share, start }: { share: PublicShareView; start: ReactNode }) {
  const { t } = useI18n();
  const lead = leadOf(share);
  return (
    <div className="sr-cover">
      <div className="sr-hero" style={lead ? { backgroundImage: `url(/art/bg_${lead}.webp)` } : share.coverUrl ? { backgroundImage: `url(${share.coverUrl})` } : undefined}>
        {lead ? <img className="sr-figure" src={`/art/fig_${lead}.webp`} alt="" /> : null}
        <div className="sr-hero-text">
          <div className="sr-badges">
            {share.serialNo ? <span className="sr-badge sr-pink">{t('share.chapterNo', { n: share.serialNo })}</span> : null}
            <span className="sr-badge sr-ink">{t('share.day', { day: share.gameDay })}</span>
            {share.tier === 'illustrated' ? <span className="sr-badge sr-lime">{t('share.illustrated')}</span> : null}
          </div>
          <h1 className="sr-cover-title">{share.title}</h1>
          {share.subtitle ? <p className="sr-cover-sub">{share.subtitle}</p> : null}
        </div>
      </div>
      {start}
    </div>
  );
}

/** After the pages: 完, and a way to play your own. */
function End({ share, report, onAgain }: { share: PublicShareView; report: Report; onAgain: () => void }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  // On the strip, 完 counts once it scrolls into view (the paged reader counts the page turn).
  useEffect(() => {
    const el = ref.current;
    if (!el || !el.closest('.sr-scroll')) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && report('end'), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [report]);
  return (
    <div className="sr-end" ref={ref}>
      <div className="sr-stamp" role="heading" aria-level={2}>
        {t('share.end.title')}
      </div>
      <p className="sr-end-body">{t('share.end.body', { title: share.title })}</p>
      <div className="sr-cta">
        <b>{t('share.end.cta')}</b>
        <p>{t('share.end.ctaBody')}</p>
        {APP_STORE_URL ? (
          <a className="store-badge" href={APP_STORE_URL} target="_blank" rel="noreferrer" onClick={() => report('cta')}>
            <AppleGlyph />
            <span>
              <b>App Store</b>
              <small>{t('landing.appStore')}</small>
            </span>
          </a>
        ) : (
          <Link className="btn btn-pink" to="/prereg?ref=share" onClick={() => report('cta')}>
            {t('share.end.prereg')}
          </Link>
        )}
      </div>
      <button className="btn btn-ghost" onClick={onAgain}>
        {t('share.end.again')}
      </button>
    </div>
  );
}

/** 閱讀設定: paged or one strip, and which way the pages turn (kept in this browser). */
function Settings({ mode, dir, onMode, onDir, onClose }: { mode: Mode; dir: Dir; onMode: (m: Mode) => void; onDir: (d: Dir) => void; onClose: () => void }) {
  const { t, lang, setLang } = useI18n();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const row = <T extends string>(label: string, value: T, options: Array<[T, string]>, set: (v: T) => void, hint?: string) => (
    <div className="sr-set-row">
      <div>
        <b>{label}</b>
        {hint ? <small>{hint}</small> : null}
      </div>
      <div className="sr-seg" role="radiogroup" aria-label={label}>
        {options.map(([k, l]) => (
          <button key={k} role="radio" aria-checked={value === k} className={value === k ? 'sr-on' : ''} onClick={() => set(k)}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="sr-sheet-wrap" onClick={onClose}>
      <div className="sr-sheet" role="dialog" aria-label={t('share.settings')} onClick={(e) => e.stopPropagation()}>
        <div className="sr-sheet-head">
          <b>{t('share.settings')}</b>
          <button className="sr-icon sr-light" aria-label={t('share.close')} onClick={onClose}>
            <CloseGlyph />
          </button>
        </div>
        {row(t('share.mode'), mode, [['paged', t('share.mode.paged')], ['scroll', t('share.mode.scroll')]], onMode)}
        {row(t('share.direction'), dir, [['ltr', t('share.ltr')], ['rtl', t('share.rtl')]], onDir, t(dir === 'rtl' ? 'share.rtlHint' : 'share.ltrHint'))}
        {/* The reader's own words; the chapter stays in the language it was drawn in. */}
        {row(t('share.language'), lang, [['zh-HK', '繁體中文'], ['zh-CN', '简体中文'], ['en', 'English']], setLang, t('share.languageHint'))}
      </div>
    </div>
  );
}

function SlidersGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function ArrowGlyph({ flip }: { flip?: boolean }) {
  return (
    <svg className="sr-arrow" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={flip ? { transform: 'scaleX(-1)' } : undefined}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
